import React, { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { isLocalModeEnabled, addLocalGroup, addLocalDonor } from '../../lib/localMode';
import './DesktopImportModal.css';

function DesktopImportModal({ isOpen, onClose, groups = [], onImportComplete, authFetch, allPeople = {} }) {
  const [step, setStep] = useState('upload'); // upload, preview, duplicates, importing, complete
  const [, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importedGroupInfo, setImportedGroupInfo] = useState(null); // Track which group we imported to
  const [duplicates, setDuplicates] = useState([]); // People that already exist
  const [duplicateAction, setDuplicateAction] = useState('skip'); // 'skip' or 'replace'
  const fileInputRef = useRef(null);

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setHeaders([]);
    setSelectedGroup(null);
    setNewGroupName('');
    setIsCreatingGroup(false);
    setProgress(0);
    setImportedCount(0);
    setError(null);
    setImportedGroupInfo(null);
    setDuplicates([]);
    setDuplicateAction('skip');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Extract people array from various JSON structures (different CRMs use different formats)
  const extractPeopleFromJSON = (data) => {
    // If it's already an array, use it directly
    if (Array.isArray(data)) {
      return data;
    }

    // Common keys used by different CRMs for people/contacts arrays
    const possibleKeys = [
      'donors', 'contacts', 'people', 'persons', 'users', 'leads',
      'customers', 'members', 'constituents', 'records', 'data',
      'Donors', 'Contacts', 'People', 'Users', 'Leads', 'Customers'
    ];

    for (const key of possibleKeys) {
      if (data[key] && Array.isArray(data[key])) {
        return data[key];
      }
    }

    // If no array found, check if the object itself looks like a single person
    if (data.name || data.Name || data.firstName || data.first_name) {
      return [data];
    }

    return null;
  };

  // Normalize a person record to extract common fields
  const normalizePerson = (record) => {
    // Build a normalized object with all original fields plus extracted common ones
    const normalized = { ...record };

    // Try to extract/normalize common fields
    // Name
    if (!normalized.name) {
      normalized.name = record.Name || record.full_name || record.fullName ||
        (record.firstName || record.first_name || record.FirstName || '') + ' ' +
        (record.lastName || record.last_name || record.LastName || '') ||
        record.display_name || record.displayName || '';
      normalized.name = normalized.name.trim();
    }

    // Flatten meeting notes if they exist
    if (record.meetingNotes && Array.isArray(record.meetingNotes)) {
      normalized._meetingNotes = record.meetingNotes
        .map(n => `${n.date || ''}: ${n.notes || ''}`)
        .join('\n');
    }

    return normalized;
  };

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;

    const fileName = selectedFile.name.toLowerCase();
    const isJSON = fileName.endsWith('.json');
    const isCSV = fileName.endsWith('.csv') || fileName.endsWith('.tsv') || fileName.endsWith('.txt');

    if (!isJSON && !isCSV) {
      setError('Please upload a CSV or JSON file');
      return;
    }

    setFile(selectedFile);
    setError(null);

    if (isJSON) {
      // Handle JSON files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          console.log('[Import] JSON parsed:', jsonData);

          const people = extractPeopleFromJSON(jsonData);
          if (!people || people.length === 0) {
            setError('Could not find people/contacts in this JSON file');
            return;
          }

          console.log('[Import] Found people:', people.length);

          // Normalize all records
          const normalizedPeople = people.map(normalizePerson);

          // Get all unique keys across all records for headers
          const allKeys = new Set();
          normalizedPeople.forEach(person => {
            Object.keys(person).forEach(key => {
              // Skip internal/complex fields
              if (!key.startsWith('_') && typeof person[key] !== 'object') {
                allKeys.add(key);
              }
            });
          });

          // Prioritize common fields first
          const priorityFields = ['name', 'Name', 'organization', 'email', 'phone', 'stage', 'totalGiven', 'askAmount', 'nextTask'];
          const sortedHeaders = [...allKeys].sort((a, b) => {
            const aIdx = priorityFields.indexOf(a);
            const bIdx = priorityFields.indexOf(b);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;
            return a.localeCompare(b);
          });

          setHeaders(sortedHeaders);
          setParsedData(normalizedPeople);
          setStep('preview');
        } catch (err) {
          console.error('[Import] JSON parse error:', err);
          setError(`Failed to parse JSON: ${err.message}`);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsText(selectedFile);
    } else {
      // Handle CSV files
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        delimitersToGuess: [',', '\t', '|', ';'],
        complete: (results) => {
          console.log('[Import] CSV parse results:', results);
          console.log('[Import] Found rows:', results.data.length);
          console.log('[Import] Headers:', results.meta.fields);
          if (results.data.length === 0) {
            setError('The file appears to be empty');
            return;
          }
          // Filter out empty rows (rows where all values are empty)
          const validRows = results.data.filter(row => {
            return Object.values(row).some(val => val && String(val).trim());
          });
          console.log('[Import] Valid rows after filtering:', validRows.length);
          setHeaders(results.meta.fields || []);
          setParsedData(validRows);
          setStep('preview');
        },
        error: (err) => {
          console.error('[Import] CSV parse error:', err);
          setError(`Failed to parse file: ${err.message}`);
        }
      });
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files[0]);
  };

  // Check for duplicates before importing
  const checkForDuplicates = () => {
    if (!selectedGroup && !newGroupName.trim()) {
      setError('Please select or create a group');
      return;
    }

    // Get names from parsed data
    const existingNames = Object.keys(allPeople).map(n => n.toLowerCase().trim());
    const foundDuplicates = [];

    parsedData.forEach((row, idx) => {
      const name = row.name || row.Name ||
        (row['First Name'] && row['Last Name'] ? `${row['First Name']} ${row['Last Name']}` : null) ||
        row['First Name'] || row.first_name || '';

      if (name && existingNames.includes(name.toLowerCase().trim())) {
        foundDuplicates.push({ index: idx, name: name.trim(), row });
      }
    });

    if (foundDuplicates.length > 0) {
      setDuplicates(foundDuplicates);
      setStep('duplicates');
    } else {
      // No duplicates, proceed directly to import
      proceedWithImport();
    }
  };

  // Proceed with actual import (after duplicate check)
  const proceedWithImport = async () => {
    setStep('importing');
    setProgress(0);
    setError(null);

    try {
      const localMode = isLocalModeEnabled();

      // Create new group if needed
      let groupId = selectedGroup;
      let groupName = groups.find(g => g.id === selectedGroup)?.name || '';

      if (isCreatingGroup && newGroupName.trim()) {
        if (localMode) {
          const newGroup = addLocalGroup(newGroupName.trim());
          groupId = newGroup.id;
          groupName = newGroupName.trim();
        } else {
          const groupRes = await authFetch('/api/groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newGroupName.trim() })
          });
          if (!groupRes.ok) throw new Error('Failed to create group');
          const groupData = await groupRes.json();
          groupId = groupData.group?.id || groupData.id;
          groupName = newGroupName.trim();
        }
      }

      // Save group info for navigation after import
      setImportedGroupInfo({ id: groupId, name: groupName });

      // Filter out duplicates if user chose to skip
      let dataToImport = parsedData;
      if (duplicateAction === 'skip' && duplicates.length > 0) {
        const duplicateIndices = new Set(duplicates.map(d => d.index));
        dataToImport = parsedData.filter((_, idx) => !duplicateIndices.has(idx));
      }

      // Send data to import endpoint
      const response = await authFetch('/api/import-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: dataToImport,
          headers: headers,
          groupId: groupId,
          duplicateAction: duplicateAction, // Tell server how to handle any remaining duplicates
          ...(localMode ? { localMode: true } : {})
        })
      });

      if (!response.ok) throw new Error('Import failed');

      // Handle streaming progress
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.progress !== undefined) {
                setProgress(data.progress);
                setImportedCount(data.imported || 0);
              }
              if (data.person) {
                // Local Mode: server has nothing to write to, so the browser
                // saves each finished record as it streams in.
                addLocalDonor(data.person);
              }
              if (data.complete) {
                setStep('complete');
                setImportedCount(data.totalImported);
              }
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              // Ignore parse errors for incomplete data
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
      setStep('preview');
    }
  };

  const handleDone = () => {
    onImportComplete?.(importedGroupInfo);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="desktop-import-backdrop" onClick={handleClose}>
      <div className="desktop-import-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="import-header">
          <div className="import-header-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="import-header-text">
            <h2>Import People</h2>
            <span>
              {step === 'upload' && 'Upload a CSV file'}
              {step === 'preview' && `${parsedData.length} people found`}
              {step === 'duplicates' && `${duplicates.length} already in contacts`}
              {step === 'importing' && 'Importing...'}
              {step === 'complete' && 'Import complete'}
            </span>
          </div>
          <button className="import-close-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="import-content">
          {error && (
            <div className="import-error">{error}</div>
          )}

          {/* Upload Step */}
          {step === 'upload' && (
            <div
              className={`import-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json,.tsv,.txt"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              <div className="dropzone-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="dropzone-text">Drag and drop your file here</p>
              <p className="dropzone-subtext">CSV, JSON, or export from another CRM</p>
            </div>
          )}

          {/* Preview Step */}
          {step === 'preview' && (
            <>
              <div className="import-preview">
                <div className="preview-table-wrapper">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {headers.slice(0, 5).map((header, idx) => (
                          <th key={idx}>{header}</th>
                        ))}
                        {headers.length > 5 && <th>...</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.slice(0, 5).map((row, idx) => (
                        <tr key={idx}>
                          {headers.slice(0, 5).map((header, hIdx) => (
                            <td key={hIdx}>{row[header] || '-'}</td>
                          ))}
                          {headers.length > 5 && <td>...</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedData.length > 5 && (
                  <p className="preview-more">and {parsedData.length - 5} more...</p>
                )}
              </div>

              <div className="import-group-section">
                <label className="group-label">Add to group:</label>
                <div className="group-options">
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      className={`group-option ${selectedGroup === group.id && !isCreatingGroup ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedGroup(group.id);
                        setIsCreatingGroup(false);
                      }}
                    >
                      {group.name}
                    </button>
                  ))}
                  <button
                    className={`group-option new ${isCreatingGroup ? 'selected' : ''}`}
                    onClick={() => {
                      setIsCreatingGroup(true);
                      setSelectedGroup(null);
                    }}
                  >
                    + New Group
                  </button>
                </div>
                {isCreatingGroup && (
                  <input
                    type="text"
                    className="new-group-input"
                    placeholder="Enter group name..."
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    autoFocus
                  />
                )}
              </div>
            </>
          )}

          {/* Duplicates Step */}
          {step === 'duplicates' && (
            <div className="import-duplicates-section">
              <p className="duplicates-message">
                {duplicates.length} {duplicates.length === 1 ? 'person' : 'people'} from your import already exist in your contacts:
              </p>
              <div className="duplicates-list">
                {duplicates.slice(0, 10).map((dup, idx) => (
                  <div key={idx} className="duplicate-item">
                    <span className="duplicate-name">{dup.name}</span>
                  </div>
                ))}
                {duplicates.length > 10 && (
                  <p className="duplicates-more">and {duplicates.length - 10} more...</p>
                )}
              </div>
              <div className="duplicate-options">
                <button
                  className={`duplicate-option ${duplicateAction === 'skip' ? 'selected' : ''}`}
                  onClick={() => setDuplicateAction('skip')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                  Skip duplicates
                  <span className="option-hint">Keep existing data</span>
                </button>
                <button
                  className={`duplicate-option ${duplicateAction === 'replace' ? 'selected' : ''}`}
                  onClick={() => setDuplicateAction('replace')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16"/>
                  </svg>
                  Replace duplicates
                  <span className="option-hint">Overwrite with imported data</span>
                </button>
              </div>
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="import-progress-section">
              <div className="progress-bar-wrapper">
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <p className="progress-text">
                Importing {importedCount} of {parsedData.length} people...
              </p>
              <p className="progress-subtext">
                Generating narratives with AI
              </p>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && (
            <div className="import-complete-section">
              <div className="complete-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <p className="complete-text">
                Successfully imported {importedCount} people
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="import-footer">
          {step === 'upload' && (
            <button className="import-btn secondary" onClick={handleClose}>
              Cancel
            </button>
          )}
          {step === 'preview' && (
            <>
              <button className="import-btn secondary" onClick={resetState}>
                Back
              </button>
              <button
                className="import-btn primary"
                onClick={checkForDuplicates}
                disabled={!selectedGroup && !newGroupName.trim()}
              >
                Import {parsedData.length} People
              </button>
            </>
          )}
          {step === 'duplicates' && (
            <>
              <button className="import-btn secondary" onClick={() => setStep('preview')}>
                Back
              </button>
              <button
                className="import-btn primary"
                onClick={proceedWithImport}
              >
                {duplicateAction === 'skip'
                  ? `Import ${parsedData.length - duplicates.length} New People`
                  : `Import All ${parsedData.length} People`}
              </button>
            </>
          )}
          {step === 'complete' && (
            <button className="import-btn primary" onClick={handleDone}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesktopImportModal;
